import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import { getStatusColor } from '@/utils/helpers';

const StatCard = ({ icon, label, value, color, link }) => (
  <Link to={link} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all group">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${color} bg-opacity-10`}>
      <i className={`fas ${icon} text-xl ${color.replace('bg-', 'text-')}`} />
    </div>
    <h3 className="text-2xl font-black text-slate-800 mb-1">{value}</h3>
    <p className="text-slate-500 text-sm font-medium">{label}</p>
  </Link>
);

const QuickAction = ({ icon, title, description, link, color }) => (
  <Link
    to={link}
    className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <i className={`fas ${icon} text-white text-lg`} />
    </div>
    <div>
      <h4 className="font-bold text-slate-800 group-hover:text-emerald-600 transition-colors">{title}</h4>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </div>
    <i className="fas fa-chevron-right text-slate-300 group-hover:text-emerald-500 ml-auto self-center transition-colors" />
  </Link>
);

const StaffDashboardHome = () => {
  const [stats, setStats] = useState({
    total_students: 0,
    paid_students: 0,
    total_staff: 0,
    total_payments: 0,
    total_revenue: 0,
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, studentsRes] = await Promise.all([
          adminAPI.getDashboardStats(),
          adminAPI.getStudents({ limit: 5 }),
        ]);
        setStats(statsRes.data.data || stats);
        setRecentStudents(studentsRes.data.data?.slice(0, 5) || []);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10">
          <h1 className="text-3xl font-black mb-2">Welcome back, Admin! 👋</h1>
          <p className="text-emerald-100 text-lg">
            Here's what's happening at the school today.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <StatCard icon="fa-users" label="Total Students" value={stats.total_students} color="bg-blue-500 text-blue-600" link="/staff/students" />
        <StatCard icon="fa-check-circle" label="Paid Students" value={stats.paid_students} color="bg-emerald-500 text-emerald-600" link="/staff/students" />
        <StatCard icon="fa-chalkboard-teacher" label="Staff" value={stats.total_staff} color="bg-purple-500 text-purple-600" link="/staff/profile" />
        <StatCard icon="fa-credit-card" label="Payments" value={stats.total_payments} color="bg-amber-500 text-amber-600" link="/staff/students" />
        <StatCard icon="fa-naira-sign" label="Revenue" value={`₦${(stats.total_revenue || 0).toLocaleString()}`} color="bg-rose-500 text-rose-600" link="/staff/students" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-5">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <i className="fas fa-bolt text-amber-500" /> Quick Actions
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <QuickAction icon="fa-user-plus" title="Register Student" description="Add a new student to the system" link="/admission" color="bg-gradient-to-br from-blue-500 to-blue-700" />
            <QuickAction icon="fa-clipboard-check" title="Mark Attendance" description="Record daily attendance" link="/staff/attendance" color="bg-gradient-to-br from-emerald-500 to-emerald-700" />
            <QuickAction icon="fa-award" title="Upload Results" description="Add or update student results" link="/staff/results" color="bg-gradient-to-br from-purple-500 to-purple-700" />
            <QuickAction icon="fa-users" title="View Students" description="Manage all students" link="/staff/students" color="bg-gradient-to-br from-amber-500 to-amber-700" />
          </div>
        </div>

        {/* Recent Students */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <i className="fas fa-user-clock text-blue-500" /> Recent Students
              </h3>
              <Link to="/staff/students" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
                See all
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentStudents.length > 0 ? recentStudents.map((student) => (
                <div key={student.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold text-sm">
                      {(student.full_name || 'S').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{student.full_name || '—'}</p>
                      <p className="text-xs text-slate-500">{student.admission_number || '—'}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${getStatusColor(student.payment_status)}`}>
                      {student.payment_status || 'N/A'}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="p-6 text-center text-slate-400 text-sm">
                  <i className="fas fa-inbox text-2xl mb-2 block" />
                  No students yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboardHome;

